"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Download, PhoneCall, CheckCircle2, Shield, Hash, AlertCircle, ArrowRight, MousePointer, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const GLOW_MS = 2800; // how long each node stays lit
const TRAVEL_MS = 2000; // packet travel time between nodes

const steps = [{
  number: 1,
  cmd: "download --tool rtw-support",
  title: "Download the Support Tool",
  summary: "Your browser saves a small, signed file from Rapid Tech Works.",
  detail: "Click the download button above. Your browser will save a small file — it appears at the top of your screen in Chrome or Edge, or in your Downloads folder on Mac. This file is safe, signed by Splashtop, and runs only for this session.",
  tip: "Can't find your download? Press Ctrl+J (Windows) or Cmd+Option+L (Mac) to open your browser's download panel.",
  icon: Download
}, {
  number: 2,
  cmd: "run ./RapidTechWorks_Support",
  title: "Open the Downloaded File",
  summary: "Double-click the file. Nothing installs permanently.",
  detail: "Find the downloaded file — usually named 'splashtopSOS.exe' on Windows or 'splashtopSOS.dmg' on Mac. Double-click it to open.",
  tip: "On Windows a blue box may ask 'Do you want to allow this app to make changes to your device?' — click Yes. This is expected and required.",
  icon: MousePointer
}, {
  number: 3,
  cmd: "permit --access --confirm",
  title: "Allow Access If Asked",
  summary: "Your OS may warn you — click through to allow the tool to run.",
  detail: "Your computer's security settings may show a warning. Since you intentionally downloaded this from our site, click 'Run', 'Allow', or 'Yes'. If you see 'Windows protected your PC', click 'More info' then 'Run anyway'.",
  tip: "If you're ever unsure about a prompt, call us at (605) 910-4836 before clicking anything — we'd rather you ask.",
  icon: Shield
}, {
  number: 4,
  cmd: "echo $SESSION_CODE",
  title: "Find Your 9-Digit Session Code",
  summary: "A window appears with a large unique number — don't close it.",
  detail: "Once the tool opens, a small window displays a large 9-digit session ID. This number changes every single time the program opens, so it only works for this call. Keep the window open throughout the session.",
  tip: "The code looks like: 123 456 789. It may show spaces or dashes — just read us all nine digits.",
  icon: Hash
}, {
  number: 5,
  cmd: "dial --support (605) 910-4836",
  title: "Call Us & Share Your Code",
  summary: "Read us your code — we'll connect and you'll watch in real time.",
  detail: "Call our support line and read your 9-digit code when we answer. We'll enter it on our end and you'll see one final prompt to allow access — click Allow. You can watch everything we do live, and end the session any time.",
  tip: "You're always in control. Press Alt+F4 or click the X on the support window to disconnect instantly.",
  icon: PhoneCall
}];

// ── Inline visuals ────────────────────────────────────────────────

function BrowserBarVisual() {
  return <div className="rounded-lg overflow-hidden border border-border bg-card">
      <div className="bg-muted/60 px-3 py-2.5 flex items-center gap-2 border-b border-border">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/20" />
          <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/20" />
        </div>
        <div className="flex-1 bg-background rounded px-2 py-1 font-mono text-xs text-muted-foreground border border-border">
          rapidtechworks.com/support
        </div>
      </div>
      <div className="px-3 py-3 flex items-center gap-2">
        <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-md px-3 py-2">
          <Download className="h-3.5 w-3.5 text-primary" />
          <span className="font-mono text-xs text-foreground">RapidTechWorks_Support.exe</span>
          <span className="font-mono text-xs text-green-600 font-semibold ml-1">✓</span>
        </div>
        <span className="font-mono text-xs text-muted-foreground">← your file</span>
      </div>
    </div>;
}
function UACVisual() {
  return <div className="rounded-lg overflow-hidden border-2 border-secondary/40 bg-card max-w-xs mx-auto">
      <div className="bg-secondary/10 border-b border-secondary/20 px-4 py-3 flex items-center gap-2">
        <Shield className="h-4 w-4 text-secondary" />
        <span className="font-mono text-sm text-secondary font-semibold">User Account Control</span>
      </div>
      <div className="p-5">
        <p className="font-default text-sm text-foreground font-semibold mb-1">
          Do you want to allow this app to make changes?
        </p>
        <p className="font-mono text-xs text-muted-foreground mb-5">SplashtopSOS.exe</p>
        <div className="flex gap-2">
          <button className="flex-1 bg-secondary text-secondary-foreground font-default text-sm py-2 rounded-md font-semibold">
            Yes ← click this
          </button>
          <button className="flex-1 bg-muted text-muted-foreground font-default text-sm py-2 rounded-md">
            No
          </button>
        </div>
      </div>
    </div>;
}
function SecurityWarningVisual() {
  return <div className="rounded-lg overflow-hidden border border-border bg-card max-w-xs mx-auto">
      <div className="px-5 py-6 text-center">
        <div className="w-11 h-11 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Shield className="h-5 w-5 text-secondary" />
        </div>
        <p className="font-default text-sm font-bold text-foreground mb-1">Windows protected your PC</p>
        <p className="font-mono text-xs text-muted-foreground mb-5 leading-relaxed">
          SmartScreen prevented an unrecognized app from starting.
        </p>
        <div className="flex flex-col gap-2 text-left">
          <span className="font-mono text-xs text-secondary underline cursor-pointer">
            More info ← click here first
          </span>
          <button className="bg-secondary text-secondary-foreground font-default text-sm py-2 rounded-md font-semibold w-full mt-1">
            Run anyway ← then this
          </button>
        </div>
      </div>
    </div>;
}
function SessionCodeVisual() {
  return <div className="rounded-lg overflow-hidden border border-border bg-card max-w-xs mx-auto">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border bg-muted/40">
        <span className="font-mono text-xs text-muted-foreground">rtw:/session --active</span>
        <div className="flex gap-1">
          <span className="w-2 h-2 rounded-full bg-primary/40" />
          <span className="w-2 h-2 rounded-full bg-secondary/40" />
          <span className="w-2 h-2 rounded-full bg-muted-foreground/20" />
        </div>
      </div>
      <div className="p-5 text-center">
        <p className="font-mono text-xs text-muted-foreground tracking-widest mb-3">// session-code</p>
        <div className="border-2 border-dashed border-primary/30 bg-primary/5 rounded-lg px-4 py-3 mb-3">
          <span className="font-mono text-2xl font-bold text-foreground tracking-widest">123 456 789</span>
        </div>
        <p className="font-mono text-xs text-muted-foreground mb-4">read this to your tech</p>
        <div className="flex items-center justify-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="font-mono text-xs text-green-600">ready for connection</span>
        </div>
      </div>
    </div>;
}
function PhoneVisual() {
  return <div className="rounded-lg border border-border bg-card p-6 max-w-xs mx-auto text-center">
      <PhoneCall className="h-8 w-8 mx-auto mb-2 text-primary" />
      <p className="font-mono text-xs text-muted-foreground tracking-widest mb-1">// call-us</p>
      <p className="font-default text-2xl font-bold text-foreground tracking-wide">(605) 910-4836</p>
      <p className="font-mono text-xs text-muted-foreground mt-2">Mon–Fri 8am–6pm · Emergency 24/7</p>
    </div>;
}
const stepVisuals = [<BrowserBarVisual />, <UACVisual />, <SecurityWarningVisual />, <SessionCodeVisual />, <PhoneVisual />];

// ── Main component ────────────────────────────────────────────────

export default function RemoteSupportGuide() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [glowNode, setGlowNode] = useState<number>(-1);
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const packetRef = useRef<HTMLDivElement>(null);
  const posRef = useRef<number[]>([]);

  // offsetTop is relative to the nearest positioned ancestor (the relative container),
  // which is the same coordinate system the absolutely-positioned packet uses.
  const measure = useCallback(() => {
    posRef.current = nodeRefs.current.map(n => n ? n.offsetTop + n.offsetHeight / 2 : 0);
  }, []);
  useEffect(() => {
    // rAF ensures the browser has laid out before we measure
    const id = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  // Re-measure after accordion settles (300ms expand animation)
  useEffect(() => {
    const t = setTimeout(measure, 350);
    return () => clearTimeout(t);
  }, [activeStep, measure]);
  useEffect(() => {
    const N = steps.length;
    let stopped = false;
    const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
    const travel = (fromPx: number, toPx: number): Promise<void> => new Promise(resolve => {
      const el = packetRef.current;
      if (!el) return resolve();
      // Snap to start instantly
      el.style.transition = "none";
      el.style.opacity = "1";
      el.style.top = `${fromPx - 8}px`; // -8 vertically centers the 16px element
      // Two rAF frames let the browser commit the snap before we apply the transition
      requestAnimationFrame(() => requestAnimationFrame(() => {
        el.style.transition = `top ${TRAVEL_MS}ms cubic-bezier(0.4, 0, 0.6, 1)`;
        el.style.top = `${toPx - 8}px`;
        setTimeout(resolve, TRAVEL_MS);
      }));
    });
    const hide = () => {
      const el = packetRef.current;
      if (!el) return;
      el.style.transition = "opacity 400ms ease";
      el.style.opacity = "0";
    };
    const run = async () => {
      // Give the rAF measure effect time to run before the first iteration
      await sleep(1200);
      while (!stopped) {
        measure(); // always use latest layout

        for (let i = 0; i < N && !stopped; i++) {
          setGlowNode(i);
          await sleep(GLOW_MS);
          if (stopped) break;
          if (i < N - 1) {
            const pos = posRef.current;
            // Only travel if we have real, increasing positions
            if (pos.length >= N && pos[i] > 0 && pos[i + 1] > pos[i]) {
              await travel(pos[i], pos[i + 1]);
            } else {
              await sleep(TRAVEL_MS); // layout not ready — just wait
            }
            if (stopped) break;
            hide();
          }
        }
        if (!stopped) {
          setGlowNode(-1);
          await sleep(1200);
        }
      }
    };
    run();
    return () => {
      stopped = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <section id="remote-support-guide" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* decorative lines */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
        <div className="absolute left-[10%] top-0 w-px h-full bg-gradient-to-b from-transparent via-secondary/10 to-transparent" />
        <div className="absolute right-[10%] top-0 w-px h-full bg-gradient-to-b from-transparent via-secondary/10 to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-3xl">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="font-mono text-sm text-primary/70 tracking-widest mb-4">
            // remote-support --guide
          </div>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-5 leading-tight">
            Got an urgent problem?<br />
            <span className="text-secondary">We'll connect in minutes.</span>
          </h2>
          <p className="font-default text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Follow these five steps to let us access your computer remotely. No tech experience needed.
          </p>
        </div>

        {/* Download CTA */}
        <div className="flex justify-center mb-16">
          <Button size="lg" className="rounded-full px-8 h-14 font-mono text-sm font-bold tracking-wide shadow-lg hover:shadow-xl transition-all group relative overflow-hidden" asChild>
            <Link to="https://download.splashtop.com/sos/SplashtopSOS.exe">
              <Download className="h-5 w-5 mr-2" />
              $ download --support-tool
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
            </Link>
          </Button>
        </div>

        {/* ── Network steps ── */}
        <div className="relative">

          {/* Vertical network line */}
          <div className="absolute left-[10px] top-[24px] bottom-[24px] w-px bg-border/50" />

          {/* Traveling packet — position & opacity driven by JS */}
          <div ref={packetRef} className="absolute z-20 pointer-events-none w-4 h-4 rounded-full bg-primary/25 border-2 border-primary ring-4 ring-primary/15" style={{
          left: "2px",
          top: "0px",
          opacity: "0"
        }} />

          <div className="space-y-4">
            {steps.map((step, i) => {
            const isOpen = activeStep === i;
            const isGlowing = glowNode === i && !isOpen;
            const Icon = step.icon;
            return <div key={i} className="flex items-start gap-5">

                  {/* Network node */}
                  <div ref={el => {
                nodeRefs.current[i] = el;
              }} className="relative z-10 flex-shrink-0 mt-5">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${isOpen ? "border-primary bg-primary/10 shadow-[0_0_0_4px_hsl(var(--primary)/0.10)]" : isGlowing ? "border-primary/60 bg-primary/5 shadow-[0_0_10px_4px_hsl(var(--primary)/0.22)]" : "border-border bg-background"}`}>
                      <div className={`w-2 h-2 rounded-full transition-all duration-300 ${isOpen ? "bg-primary scale-100" : isGlowing ? "bg-primary/50 scale-110" : "bg-muted-foreground/30 scale-100"}`} />
                    </div>
                  </div>

                  {/* Step card */}
                  <div className={`flex-1 min-w-0 rounded-xl border overflow-hidden transition-all duration-300 ${isOpen ? "border-border/70 bg-card shadow-sm" : isGlowing ? "border-primary/25 bg-card/60" : "border-border/30 bg-card/40 hover:border-border/55 hover:bg-card/70"}`}>
                    {/* Header row */}
                    <button className="w-full text-left px-5 py-4 flex items-start gap-3.5" onClick={() => setActiveStep(isOpen ? null : i)}>
                      {/* Step icon */}
                      <div className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-md flex items-center justify-center transition-all duration-300 ${isOpen ? i % 2 === 0 ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary" : isGlowing ? "bg-primary/8 text-primary/60" : "bg-muted text-muted-foreground"}`}>
                        <Icon className="h-4 w-4" />
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Command line */}
                        <div className="font-mono text-xs text-muted-foreground/55 mb-1 flex items-center gap-1">
                          <span className={`transition-colors ${isOpen ? i % 2 === 0 ? "text-primary" : "text-secondary" : isGlowing ? "text-primary/50" : "text-muted-foreground/40"}`}>
                            $
                          </span>
                          <span>{step.cmd}</span>
                        </div>
                        {/* Title */}
                        <p className="font-default font-semibold text-foreground text-base leading-snug">
                          {step.title}
                        </p>
                        {/* Summary */}
                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-1"}`}>
                          <p className="font-mono text-xs text-muted-foreground/50 truncate">
                            // {step.summary}
                          </p>
                        </div>
                      </div>

                      <ChevronDown className={`h-4 w-4 text-muted-foreground/50 flex-shrink-0 mt-1.5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Expanded output */}
                    <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                      <div className="overflow-hidden">
                        <div className="border-t border-border/40 px-5 pt-5 pb-6">
                          <div className="grid md:grid-cols-2 gap-6 items-start">

                            {/* Text output */}
                            <div>
                              <div className="font-mono text-xs text-muted-foreground/40 tracking-widest mb-2">
                                // output
                              </div>
                              <p className="font-default text-base text-foreground/80 leading-relaxed mb-5">
                                {step.detail}
                              </p>

                              <div className="font-mono text-xs text-muted-foreground/40 tracking-widest mb-2">
                                // tip
                              </div>
                              <div className="flex items-start gap-3 border border-primary/15 bg-primary/5 rounded-lg px-4 py-3">
                                <AlertCircle className="h-4 w-4 text-primary flex-shrink-0 mt-px" />
                                <p className="font-default text-sm text-foreground/75 leading-relaxed">
                                  {step.tip}
                                </p>
                              </div>
                            </div>

                            {/* Visual */}
                            <div>{stepVisuals[i]}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>;
          })}
          </div>
        </div>

        {/* Bottom CTA — floating card */}
        <div className="mt-14 relative">
          <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-60 blur-sm pointer-events-none" />
          <div className="absolute -inset-3 rounded-3xl bg-primary/5 blur-2xl opacity-50 pointer-events-none" />

          <div className="relative bg-card border border-border/60 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.12),0_24px_64px_-16px_rgba(0,0,0,0.18)] overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-3.5 border-b border-border/40 bg-muted/30">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-primary/40 border border-primary/20" />
                <span className="w-2.5 h-2.5 rounded-full bg-secondary/40 border border-secondary/20" />
                <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/20 border border-muted-foreground/10" />
              </div>
              <span className="font-mono text-xs text-muted-foreground/50 ml-2">rtw:/support --ready</span>
            </div>

            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/30 rounded-tl-3xl" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary/30 rounded-tr-3xl" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary/30 rounded-bl-3xl" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/30 rounded-br-3xl" />

            <div className="p-8 md:p-10 text-center">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-4 text-primary" />
              <div className="font-mono text-sm text-primary/70 tracking-widest mb-3">
                // ready-when-you-are
              </div>
              <h3 className="text-2xl font-default font-bold text-foreground mb-3">
                Ready when you are
              </h3>
              <p className="font-default text-base text-muted-foreground mb-7 max-w-md mx-auto leading-relaxed">
                Once you have the support tool open and your 9-digit code ready, give us a call and we'll get you back up and running fast.
              </p>
              <Button size="lg" className="rounded-full px-8 h-12 font-mono text-sm font-bold tracking-wide shadow-md hover:shadow-lg transition-all group relative overflow-hidden" asChild>
                <Link to="tel:6059104836">
                  <PhoneCall className="h-4 w-4 mr-2" />
                  $ call (605) 910-4836
                  <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}